import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchTermSheet, generateTermSheetPDF, deleteTermSheet, updateTermSheet } from '../utils/api';
import { isViewer } from '../utils/auth';

const TermSheetDetails = ({ currentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const ts = await fetchTermSheet(id);
        setData(ts);
      } catch (e) {
        setError('Failed to load term sheet');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleDownload = async () => {
    try {
      const blob = await generateTermSheetPDF(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `term-sheet-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      alert('Failed to generate PDF');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this term sheet?')) return;
    try {
      await deleteTermSheet(id);
      navigate('/term-sheets');
    } catch (e) {
      alert('Failed to delete term sheet');
    }
  };

  const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : 'N/A');

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 8, border: '1px solid #fecaca' }}>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>{data.companyName}</h1>
          <p>Term Sheet Details</p>
        </div>
        <div className="page-actions">
          <button className="btn" onClick={() => navigate('/term-sheets')}>Back</button>
          <button className="btn" onClick={handleDownload}>Download PDF</button>
          {!isViewer() && (
            <>
              <button className="btn btn-secondary" onClick={() => navigate('/term-sheets') && null}>Edit</button>
              <button className="btn btn-secondary" onClick={handleDelete}>Delete</button>
            </>
          )}
        </div>
      </div>

      <div className="info-section">
        <div className="info-section-header">
          <h3>Sub-Lease Details</h3>
        </div>
        <div className="info-grid">
          <div className="info-card"><div className="info-card-label">Sub-Lessee</div><div className="info-card-value">{data.subLesseeName}</div></div>
          <div className="info-card"><div className="info-card-label">Registered Name</div><div className="info-card-value">{data.registeredName}</div></div>
          <div className="info-card"><div className="info-card-label">Location</div><div className="info-card-value">{data.address}</div></div>
          <div className="info-card"><div className="info-card-label">Rentable Seats</div><div className="info-card-value">{data.rentableSeats}</div></div>
          <div className="info-card"><div className="info-card-label">Monthly Rent</div><div className="info-card-value">{formatCurrency(data.monthlyRent)}</div></div>
          <div className="info-card"><div className="info-card-label">Security Deposit</div><div className="info-card-value">{formatCurrency(data.securityDeposit)}</div></div>
          <div className="info-card"><div className="info-card-label">Lock-in Period</div><div className="info-card-value">{data.lockInPeriod} months</div></div>
          <div className="info-card"><div className="info-card-label">Escalation</div><div className="info-card-value">{data.escalationPercentage}% P.A</div></div>
          <div className="info-card"><div className="info-card-label">Rent Start Date</div><div className="info-card-value">{formatDate(data.rentStartDate)}</div></div>
          <div className="info-card"><div className="info-card-label">Handover Date</div><div className="info-card-value">{formatDate(data.handoverDate)}</div></div>
          <div className="info-card"><div className="info-card-label">Notice Period</div><div className="info-card-value">{data.noticePeriod} days</div></div>
          <div className="info-card"><div className="info-card-label">Status</div><div className="info-card-value">{data.status}</div></div>
        </div>
      </div>

      <div style={{ color: '#6b7280', fontSize: 12 }}>
        Created: {formatDate(data.createdAt)}{data.createdBy ? ` by ${data.createdBy.name}` : ''}
      </div>
    </div>
  );
};

export default TermSheetDetails;
